import {ChartConfiguration} from "chart.js";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {BaseChartDirective} from "ng2-charts";
import {ChartDataProvider} from "../../utils/chart-data-provider";
import lodash, {cloneDeep} from "lodash";
import {ChartType} from "chart.js/dist/types";
import {ChartUtilsService} from "./chart-utils.service";
import {MeasurementData, MeasurementDataset} from "./base-measurement-chart";
import {View} from "../../common/view";
import {Handler} from "../../common/handler";

@Component({
  template: ''
})
@Handler()
export class BaseChart<T extends ChartType = ChartType> extends View implements AfterViewInit, OnChanges {
  protected ngZone: NgZone = inject(NgZone);
  protected chartUtils: ChartUtilsService = inject(ChartUtilsService);
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild(BaseChartDirective) chartDirective: BaseChartDirective;
  _data: MeasurementData;
  @Input() chartTitle: string = "";
  @Input() dataProvider: ChartDataProvider;
  @Input() scales: {
    [key: string]: { };
  }
  @Input() fullScreen: boolean;
  @Input() directiveView: View;
  @Input() deepCloneData: boolean = true;
  @Input() singleUpdateOnDataChange: boolean = false;

  tooltipCentered: boolean = false;
  private hasData: boolean;
  private loaded: boolean;
  refreshOptionsOnDataChange: boolean = false;

  chartData = {
    datasets: []
  }

  getOptions(): ChartConfiguration<T>['options'] {
    return {} as any;
  }

  private debounceTime: number = 100;
  private debounceUpdateChart = lodash.debounce(this.updateChart, this.debounceTime);

  get showChart() {
    return this.hasData || !this.loaded;
  }

  @Input()
  set data(data: MeasurementData) {
    this._data = data;
    if (this._data && this.chartDirective) {
      this.ngZone.runOutsideAngular(() => {
        this.debounceUpdateChart(data);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.ngZone.runOutsideAngular(() => this.refreshOptions());
  }

  updateChart(data: MeasurementData) {
    this.ngZone.runOutsideAngular(() => {
      this.chartData.datasets = copyChartDatasets(data, this.deepCloneData);
      this.chartDirective.data.datasets = this.chartData.datasets.map(d => d.dataset);
      if (this.hasData !== this.chartHasData()) {
        this.hasData = this.chartHasData();
        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      }
      this.chartDirective.data.labels = data.labels || [];
      this.updateChartWhenInitialised(false);
    });
  }

  updateChartWhenInitialised = (waiting: boolean) => {
    if (!this.singleUpdateOnDataChange) {
      this.refreshOptions();
    }
    if (!this.chartDirective.chart) {
      this.chartDirective.render();
      setTimeout(() => this.updateChartWhenInitialised(true), 100);
      return;
    }
    if (waiting) {
      setTimeout(() => this.debounceUpdateChart(this._data), 100);
      return;
    }
    if (this.singleUpdateOnDataChange) {
      this.chartDirective.chart.options = this.getOptions() as any;
    }
    this.chartDirective.chart["fullScreen"] = this.fullScreen;
    this.chartDirective.chart["tooltipCentered"] = this.tooltipCentered;
    this.loaded = true;
    this.chartDirective.chart.update();
    if (this.refreshOptionsOnDataChange) {
      this.ngZone.runOutsideAngular(() => this.refreshOptions());
    }
  }

  protected refreshOptions(waiting: boolean = false) {
    if (!this.chartDirective) {
      return;
    }
    if (!this.chartDirective.chart) {
      this.chartDirective.render();
      setTimeout(() => this.refreshOptions(true), 100);
      return;
    }
    if (waiting) {
      setTimeout(() => this.refreshOptions(false), 100);
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.chartDirective.chart.options = this.getOptions() as any;
      setTimeout(() => this.chartDirective.chart?.update(), 0);
    });
  }

  ngAfterViewInit() {
    this.dataProvider.aggregatedDataChanged.subscribe(d => this.data = d);
    if (this.dataProvider.lastValue?.datasets?.length > 0) {
      this.data = this.dataProvider.lastValue;
    }
    this.ngZone.runOutsideAngular(() => this.refreshOptions());
    if (this.fullScreen) {
      [0, 100, 350].forEach(delay => setTimeout(() => this.chartDirective?.chart?.resize(), delay));
    }
  }

  chartHasData = () => this.chartData.datasets.some(d => d.data?.length > 0);
}

export function copyChartDatasets(data: MeasurementData, deepCloneData: boolean): MeasurementDataset[] {
  const datasets = data.datasets.filter(Boolean) || [];
  return deepCloneData ? cloneDeep(datasets) : datasets.map(dataset => ({
    ...dataset,
    dataset: {
      ...dataset.dataset,
      data: [...(dataset.dataset.data || [])]
    }
  }));
}
